/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Pagination from '../../components/UI/molecules/Pagination';
import classnames from 'classnames';
import { useAuth } from '@lb-classroom/feature-auth';
import { guessTimezone } from '../../lib/date-utils';
import * as TutorActions from '../../actions/tutor';
import * as TimeslotActions from '../../actions/timeslot';
import * as FlashActions from '../../actions/flash';
import TutorFilter from '../../components/Tutors/Filter';
import TutorCards from '../../components/Tutors/Cards';
import Select from '../../components/UI/atoms/form/Select';
import { withTranslation } from 'next-i18next';
import { serverSideTranslations } from '@lib/i18n';
import { TFunction } from 'i18next';
import { useTutorListGuard } from '../../components/Tutors/use-tutor-list-guard';

function mapStateToProps(state) {
  const { tutor, geoData, onlineStatus, tutorListTimeslot } = state;
  const { tutors, count } = tutor;
  const { timeZone } = geoData.geoData;
  const { onlineStatuses } = onlineStatus;
  const { lastFilterOpts, slotStatuses, loading } = tutorListTimeslot;
  return {
    tutors,
    numOfTutors: count,
    loading,
    timeZone,
    onlineStatuses,
    slotStatuses,
    lastFilterOpts,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    tutorActions: bindActionCreators<any, any>(TutorActions, dispatch),
    timeslotActions: bindActionCreators<any, any>(TimeslotActions, dispatch),
    flashActions: bindActionCreators<any, any>(FlashActions, dispatch),
  };
}

interface TutorListProps {
  t: TFunction;
  timeZone: string;
  loading: boolean;
  tutors: any[];
  numOfTutors: number;
  onlineStatuses: Record<string, unknown>;
  slotStatuses: any[];
  lastFilterOpts: any;
  timeslotActions: any;
  flashActions: any;
}

const TutorList = (props: TutorListProps) => {
  const {
    t,
    timeZone,
    loading,
    tutors,
    numOfTutors,
    onlineStatuses,
    slotStatuses,
    lastFilterOpts,
    timeslotActions,
    flashActions,
  } = props;
  const router = useRouter();
  const { date, startTime, endTime, page, sort } = router.query;
  const [state, setState] = useState({
    chosenDate: date || 0,
    chosenTimeRange: [
      startTime ? Number(startTime) : 1,
      endTime ? Number(endTime) : 49,
    ],
    chosenDuration: 0, // 25 minutes: 0 50 minutes: 1
    pageNum: page ? Number(page) : 1,
    sortOption: sort || 'availability',
  });
  const { chosenDate, chosenTimeRange, sortOption, pageNum, chosenDuration } =
    state;
  const { currentUser, loading: authLoading } = useAuth();

  useTutorListGuard({ currentUser, flashActions, setState });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only refetch when timeZone changes - state and fetchSlotStatus are captured intentionally
  useEffect(() => {
    if (timeZone) {
      fetchSlotStatus(timeZone, state);
    }
  }, [timeZone]);

  const setStatePromise = (newState) => {
    return new Promise((resolve) => {
      setState({
        ...state,
        ...newState,
      });
      resolve('state set');
    });
  };

  const onUpdateChosenDate = async (date) => {
    const newState = {
      ...state,
      chosenDate: date,
      pageNum: 1,
    };
    await setStatePromise(newState);
    changeUrl(newState);
    fetchSlotStatus(timeZone || guessTimezone(), newState);
  };

  const onUpdateChosenTimeRange = async (range) => {
    if (
      !lastFilterOpts.startTime ||
      lastFilterOpts.startTime !== range[0] ||
      lastFilterOpts.endTime !== range[1]
    ) {
      const newState = {
        ...state,
        chosenTimeRange: range,
        pageNum: 1,
      };
      await setState(newState);
      changeUrl(newState);
      fetchSlotStatus(timeZone || guessTimezone(), newState);
    }
  };

  const onUpdateChosenDuration = async (duration) => {
    const studentProfile = currentUser ? currentUser.student_profile : null;
    if (!studentProfile?.is_trial) {
      await setStatePromise({
        ...state,
        chosenDuration: duration,
      });
    } else {
      if (duration === 1) {
        flashActions.addMessage(t('flash.fiftyMinsClassNotAvailable'), {
          type: 'error',
          sticky: true,
          autoDisappear: true,
        });
      }
    }
  };

  const onUpdateSortOption = async (value) => {
    const newState = {
      ...state,
      sortOption: value,
      pageNum: 1,
    };
    await setStatePromise(newState);
    changeUrl(newState);
    fetchSlotStatus(timeZone || guessTimezone(), newState);
  };

  const transitionPage = async (page) => {
    const newState = {
      ...state,
      slotStatus: [],
      pageNum: Number(page),
    };
    await setStatePromise(newState);
    changeUrl(newState);
    fetchSlotStatus(timeZone || guessTimezone(), newState);
  };

  const handleMoveRange = async (range) => {
    await setStatePromise({
      ...state,
      chosenTimeRange: range,
    });
  };

  const fetchSlotStatus = (timeZone, newState) => {
    const opts = {
      date: Number(newState.chosenDate),
      startTime: newState.chosenTimeRange[0],
      endTime: newState.chosenTimeRange[1],
      page: newState.pageNum,
      sortOption: newState.sortOption,
    };
    timeslotActions.fetch(timeZone, opts);
  };

  const changeUrl = (newState) => {
    const location = window.location;
    let baseUrl = location.href;
    baseUrl = baseUrl.replace(/\?.*/, '');
    baseUrl += '?';
    const opts = {
      date: newState.chosenDate,
      page: newState.pageNum,
      startTime: newState.chosenTimeRange[0],
      endTime: newState.chosenTimeRange[1],
      sort: newState.sortOption,
      duration: newState.chosenDuration,
    };
    let first = true;
    for (const key of Object.keys(opts)) {
      if (opts[key]) {
        if (!first) {
          baseUrl += '&';
        }
        baseUrl += `${key}=${opts[key]}`;
        first = false;
      }
    }
    window.history.pushState(opts, '', baseUrl);
    window.scroll(0, 0);
  };

  const TutorSortProps = {
    options: [
      {
        key: 'sortTutors0',
        name: t('student.tutorList.sortByAvailability'),
        value: 'availability',
      },
      {
        key: 'sortTutors1',
        name: t('student.tutorList.sortByPopularity'),
        value: 'popularity',
      },
    ],
    className: '',
    id: 'sort-tutor',
    value: sortOption,
    updateHandler: onUpdateSortOption,
  };
  const tutorFilterProps = {
    timeZone,
    chosenDate,
    chosenTimeRange,
    chosenDuration,
    handleMoveRange,
    onUpdateChosenDate,
    onUpdateChosenTimeRange,
    onUpdateChosenDuration,
  };
  const numOfTutorsSentence = t('student.tutorList.numOfTutors', {
    count: parseInt(String(numOfTutors), 10),
    smart_count: parseInt(String(numOfTutors), 10),
  });
  const containerClass = classnames('bg-light-gray', { loading });
  // Use consistent element to avoid hydration mismatch.
  // During SSR and initial hydration, authLoading is true and currentUser is null.
  // We render the same <h1> element in both cases and conditionally show content
  // based on auth state only after hydration completes.
  const numOfTutorsPart =
    !authLoading && currentUser ? (
      <div className="text-lg">{numOfTutorsSentence}</div>
    ) : (
      <h1 style={{ fontSize: '24px', lineHeight: '36px' }}>
        {t('student.tutorList.ourTutors')}
      </h1>
    );
  return (
    <>
      <Head>
        <title>{t('title.tutors')}</title>
        <meta name="description" content={t('description.tutors')} />
      </Head>
      <div className={containerClass}>
        <TutorFilter {...tutorFilterProps} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8 py-4">
          <div className="tutor-cards-header flex justify-between">
            <div>{numOfTutorsPart}</div>
            <div>
              <Select {...TutorSortProps} />
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <TutorCards
            tutors={tutors}
            slotStatuses={slotStatuses}
            duration={chosenDuration}
            onlineStatuses={onlineStatuses}
            timeZone={timeZone}
          />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8 mt-4 pb-16">
          <div className="flex items-center justify-center">
            <Pagination
              current={pageNum}
              pageSize={24}
              total={numOfTutors}
              showSizeChanger={false}
              onChange={(page, _pageSize) => transitionPage(page)}
              showTotal={(total, range) => {
                return (
                  <span>
                    {t('shared.pagination.result', {
                      beginCount: range[0].toString(),
                      endCount: range[1].toString(),
                      totalCount: total.toString(),
                      something: t('general.tutorsDowncase'),
                    })}
                  </span>
                );
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const wrapped = withTranslation('common')(TutorList);

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      // Will be passed to the page component as props
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
